import { trackMediaListening, trackSpeechListening } from '@/utils/listeningActivity'
import { apiPostBlob } from '@/composables/useApi'

const DICTIONARY_AUDIO_HOST = 'api.dictionaryapi.dev'
const DICTIONARY_AUDIO_PATH = '/media/pronunciations/'

let activeAudio = null
let activeObjectUrl = ''
const pronunciationCache = new Map()

function releaseActiveAudio() {
  if (activeAudio) {
    activeAudio.pause()
    activeAudio.currentTime = 0
    activeAudio = null
  }
  if (activeObjectUrl) {
    URL.revokeObjectURL(activeObjectUrl)
    activeObjectUrl = ''
  }
}

async function playAudioUrl(url) {
  const audio = new Audio(url)
  trackMediaListening(audio)
  audio.preload = 'auto'
  activeAudio = audio
  await audio.play()
}

function speakWithBrowser(text) {
  const spokenText = String(text || '').trim()
  if (
    !spokenText
    || typeof window === 'undefined'
    || !window.speechSynthesis
    || typeof window.SpeechSynthesisUtterance !== 'function'
  ) {
    return Promise.reject(new Error('Browser speech synthesis is unavailable'))
  }

  window.speechSynthesis.cancel()
  const utterance = new window.SpeechSynthesisUtterance(spokenText)
  utterance.lang = 'en-US'
  utterance.rate = 0.9
  trackSpeechListening(utterance)

  const voices = window.speechSynthesis.getVoices()
  utterance.voice = voices.find((voice) => voice.lang === 'en-US')
    || voices.find((voice) => voice.lang?.startsWith('en'))
    || null

  return new Promise((resolve, reject) => {
    utterance.onend = resolve
    utterance.onerror = (event) => reject(event.error || new Error('Speech synthesis failed'))
    window.speechSynthesis.speak(utterance)
  })
}

export function getVocabularyAudioUrl(source) {
  const rawUrl = String(source || '').trim()
  if (!rawUrl || rawUrl.startsWith('/api/vocabulary/audio?')) return rawUrl

  try {
    const url = new URL(rawUrl, window.location.origin)
    if (
      url.hostname === DICTIONARY_AUDIO_HOST
      && url.pathname.startsWith(DICTIONARY_AUDIO_PATH)
    ) {
      url.protocol = 'https:'
      return `/api/vocabulary/audio?url=${encodeURIComponent(url.toString())}`
    }
  } catch {
    return rawUrl
  }

  return rawUrl
}

export async function playVocabularyAudio(source, fallbackText) {
  const audioUrl = getVocabularyAudioUrl(source)
  const spokenText = String(fallbackText || '').trim()

  releaseActiveAudio()
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    window.speechSynthesis.cancel()
  }

  if (spokenText) {
    try {
      const cacheKey = spokenText.toLowerCase()
      let blob = pronunciationCache.get(cacheKey)
      if (!blob) {
        blob = await apiPostBlob('/api/vocabulary/pronunciation', { word: spokenText })
        if (pronunciationCache.size >= 100) {
          pronunciationCache.delete(pronunciationCache.keys().next().value)
        }
        pronunciationCache.set(cacheKey, blob)
      }
      activeObjectUrl = URL.createObjectURL(blob)
      await playAudioUrl(activeObjectUrl)
      return
    } catch {
      releaseActiveAudio()
    }
  }

  if (audioUrl) {
    try {
      await playAudioUrl(audioUrl)
      return
    } catch {
      releaseActiveAudio()
    }
  }

  await speakWithBrowser(spokenText)
}
